import type { JobCostByCategory, JobCostCategory } from '@/types/settings';

/**
 * What a job is expected to cost, from three sources that must never be added
 * together blindly:
 *
 * - **Estimated** — the job's lines (`costDocument`): the plan.
 * - **Recorded** — the job cost ledger: approved expenses, purchase receipts
 *   and bill variances, each counted once on the server.
 * - **On order** — open purchase orders still to be received.
 *
 * A part bought for a line is both in the estimate and, once bought, in the
 * recorded cost. Adding the two counted it twice, which is what the Financials
 * card used to do. Instead, per category, the job is projected to cost the
 * larger of the plan and what is already spent or committed: spending past the
 * estimate shows as an overrun, and a plan not yet purchased still counts.
 * Until parts used from stock are recorded, the estimate is also what stands in
 * for material taken off a truck.
 *
 * Labor keeps its estimated cost: labor actuals live in time entries, not the
 * ledger. Categories with no estimate (subcontractors, permits, freight,
 * rentals, travel, disposal, other) are what was recorded plus what is on
 * order.
 */
export type ProjectionRowKey = 'labor' | 'material' | 'equipment' | 'other';

export interface ProjectionRow {
  key: ProjectionRowKey;
  label: string;
  estimated: number;
  recorded: number;
  onOrder: number;
  /** The figure the job's profit uses. */
  projected: number;
}

export interface JobCostProjection {
  rows: ProjectionRow[];
  estimated: number;
  recorded: number;
  onOrder: number;
  projected: number;
}

const OTHER_CATEGORIES: JobCostCategory[] = [
  'SUBCONTRACTOR',
  'EQUIPMENT_RENTAL',
  'TRAVEL',
  'PERMIT',
  'DISPOSAL',
  'FREIGHT',
  'OTHER',
];

const cents = (value: number) => Math.round(value * 100) / 100;
const pick = (costs: Partial<JobCostByCategory> | undefined, key: JobCostCategory) =>
  costs?.[key] ?? 0;
const sumOf = (rows: ProjectionRow[], field: keyof Omit<ProjectionRow, 'key' | 'label'>) =>
  cents(rows.reduce((sum, row) => sum + row[field], 0));

export function projectJobCost(
  estimate: { labor: number; material: number; equipment: number },
  recorded?: Partial<JobCostByCategory>,
  onOrder?: Partial<JobCostByCategory>,
): JobCostProjection {
  const planned = (
    key: 'material' | 'equipment',
    label: string,
    category: JobCostCategory,
  ): ProjectionRow => {
    const estimated = cents(estimate[key]);
    const spent = cents(pick(recorded, category));
    const ordered = cents(pick(onOrder, category));
    return {
      key,
      label,
      estimated,
      recorded: spent,
      onOrder: ordered,
      projected: cents(Math.max(estimated, spent + ordered)),
    };
  };
  const otherRecorded = cents(
    OTHER_CATEGORIES.reduce((sum, category) => sum + pick(recorded, category), 0),
  );
  const otherOnOrder = cents(
    OTHER_CATEGORIES.reduce((sum, category) => sum + pick(onOrder, category), 0),
  );
  const rows: ProjectionRow[] = [
    {
      key: 'labor',
      label: 'Labor',
      estimated: cents(estimate.labor),
      recorded: 0,
      onOrder: 0,
      projected: cents(estimate.labor),
    },
    planned('material', 'Materials', 'MATERIAL'),
    planned('equipment', 'Equipment', 'EQUIPMENT'),
    {
      key: 'other',
      label: 'Other costs',
      estimated: 0,
      recorded: otherRecorded,
      onOrder: otherOnOrder,
      projected: cents(otherRecorded + otherOnOrder),
    },
  ];
  return {
    rows,
    estimated: sumOf(rows, 'estimated'),
    recorded: sumOf(rows, 'recorded'),
    onOrder: sumOf(rows, 'onOrder'),
    projected: sumOf(rows, 'projected'),
  };
}
